import * as React from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';
import { FormGroup, Grid, Typography } from '@mui/material';

/**
 * One of the three possible checkbox states.
 */
interface CheckboxStatus {
    /** Numeric identifier used to cycle between states. */
    id: number;
    /** `true`/`false` for checked/unchecked, `null` for indeterminate. */
    checked: boolean | null;
    /** Whether the MUI `Checkbox` renders as indeterminate. */
    indeterminate: boolean;
}

/**
 * A single checkbox definition passed via {@link MCheckboxGroupProps.data}.
 */
interface MCheckboxGroupItem {
    /** Unique field name, also used as the `Checkbox`'s `name` and React key suffix. */
    name: string;
    /** Label rendered next to the checkbox. */
    label?: React.ReactNode;
    /** Allows cycling through an indeterminate ("null") state. Defaults to `false`. */
    nullable?: boolean;
    /** Initial checked state. `null` implies a tri-state (indeterminate-capable) checkbox. Defaults to `false`. */
    defaultValue?: boolean | null;
    /** When `true`, the checkbox is omitted from render. */
    hidden?: boolean;
}

/** An override applied via {@link MCheckboxGroupProps.defaultValue}. */
interface MCheckboxGroupValueOverride {
    /** Matches {@link MCheckboxGroupItem.name}. */
    name: string;
    /** Checked state to apply for the matching item. */
    value: boolean | null;
}

interface FormattedCheckbox extends MCheckboxGroupItem {
    nullable: boolean;
    defaultValue: boolean | null;
    allowNull: boolean;
    defaultItem: CheckboxStatus;
    statuses: CheckboxStatus[];
    isPush: boolean;
}

/**
 * Props for {@link MCheckboxGroup}.
 */
interface MCheckboxGroupProps extends Omit<CheckboxProps, 'checked' | 'indeterminate' | 'onChange' | 'defaultValue' | 'name'> {
    /** Checkbox definitions rendered in a row. */
    data: MCheckboxGroupItem[];
    /**
     * Called with the list of "active" checkboxes (checked, or unchecked when
     * {@link MCheckboxGroupItem.nullable} makes `false` a meaningful filter value).
     *
     * @param values - `{ name, value }` pairs for every pushed checkbox.
     */
    onChange: (values: { name: string; value: boolean | null }[]) => void;
    /** Group label rendered to the left of the checkboxes. */
    label?: React.ReactNode;
    /** Overrides applied on top of each item's own `defaultValue`. */
    defaultValue?: MCheckboxGroupValueOverride[];
}

const defaultStatus: CheckboxStatus[] = [
    { id: 0, checked: null, indeterminate: true },
    { id: 1, checked: true, indeterminate: false },
    { id: 2, checked: false, indeterminate: false }
];

/**
 * A row of MUI {@link Checkbox} controls, each optionally supporting the same
 * tri-state checked / unchecked / indeterminate cycle as {@link MCheckbox},
 * reporting only the "meaningful" checked values back via `onChange`.
 */
const MCheckboxGroup: React.FC<MCheckboxGroupProps> = (props) => {
    const { data, onChange, label, defaultValue, ...params } = props;

    const [dataFormatter, setDataFormatter] = React.useState<FormattedCheckbox[]>([]);
    const [item, setItem] = React.useState<FormattedCheckbox[]>([]);

    function formatCheckBoxes (cb: MCheckboxGroupItem, defaultVal?: boolean | null): FormattedCheckbox {
        const nullable = typeof cb.nullable === 'undefined' ? false : cb.nullable; // default value = false
        let __defaultVal: boolean | null;
        let hidden = cb.hidden;
        if (typeof defaultVal !== 'undefined') {
            hidden = false;
            __defaultVal = defaultVal;
        } else {
            __defaultVal = typeof cb.defaultValue === 'undefined' ? false : cb.defaultValue; // default value = false
            if (__defaultVal !== true) {
                if (!defaultValue) __defaultVal = false;
                else if (defaultValue.some(x => x.name === cb.name)) __defaultVal = true;
            }
        }
        // formatter....
        let allowNull = nullable;
        const statuses = defaultStatus.map((m) => Object.assign({}, m)); // clone array
        if (__defaultVal === null) allowNull = true;
        if (!allowNull) statuses.shift(); // remove isnull;
        let defaultItem = statuses.find(x => x.checked === __defaultVal) as CheckboxStatus;
        const isPush = !!defaultItem.checked; // should add into filter or not
        const newCb: FormattedCheckbox = { ...cb, nullable, defaultValue: __defaultVal, allowNull, defaultItem, statuses, isPush };
        const _defaultItem = changeDefaultIfAny(newCb);
        if (_defaultItem) {
            defaultItem = _defaultItem;
        }
        return { ...newCb, defaultItem, isPush, hidden };
    }

    /**
     * First set default value, then change default value
     */
    function changeDefaultIfAny (checkbox: FormattedCheckbox): CheckboxStatus | undefined {
        if (checkbox.defaultItem.checked !== null && checkbox.defaultValue === null ||
            checkbox.defaultItem.checked === null && checkbox.defaultValue !== null
        ) {
            return checkbox.statuses.find(x => x.checked === checkbox.defaultValue);
        }
    }

    function getValue (data: FormattedCheckbox[]): { name: string; value: boolean | null }[] {
        return data.reduce<{ name: string; value: boolean | null }[]>((final, cb) => {
            if (cb.defaultItem.checked !== null && cb.isPush) {
                final.push({ name: cb.name, value: cb.defaultItem.checked });
            }
            return final;
        }, []);
    }

    React.useEffect(() => {
        if (data) {
            const newData = data.map(cb => formatCheckBoxes(cb));
            setDataFormatter(newData);
        }
    }, [data]);

    React.useEffect(() => {
        if (dataFormatter.length) {
            const newDateFormatter = item.length ? item : dataFormatter;
            const newData = defaultValue ? newDateFormatter.map(cb => {
                const itemChanged = defaultValue.find(x => x.name === cb.name);
                if (itemChanged) return formatCheckBoxes(cb, itemChanged.value);
                return { ...cb };
            }) : newDateFormatter;
            setItem(newData);
        }
    }, [defaultValue, dataFormatter]);

    React.useEffect(() => {
        onChange(getValue(item));
    }, [item]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked, name } = event.target;
        const cb = item.find(x => x.name === name);
        if (!cb) return;
        let next: CheckboxStatus | undefined;
        if (cb.allowNull) {
            let nextId = cb.defaultItem.id + 1;
            if (nextId > 2) nextId = 0;
            next = cb.statuses.find(x => x.id === nextId);
        } else {
            next = cb.statuses.find(x => x.checked === checked);
        }
        if (!next) return;
        let isPush = true;
        if (!cb.allowNull && checked === false) isPush = false; // Case true or false: if value = false => ignore
        setItem((prev) => prev.map(x => x.name === name ? { ...x, defaultItem: next as CheckboxStatus, isPush } : x));
    };

    return (
        <Grid container columnSpacing={1} rowSpacing={0} className={params.className}>
            <Grid alignContent={'center'} sx={{ marginRight: '5px' }}>
                <label className="MuiFormLabel-root"><b>{label}</b></label>
            </Grid>
            <Grid size={10}>
                <FormGroup row={true}>
                    {
                        item.filter(x => !x.hidden).map(cb => {
                            return <FormControlLabel key={`key-${cb.name}`}
                                control={
                                    <Checkbox
                                        {...params}
                                        name={cb.name}
                                        size='small'
                                        checked={cb.defaultItem.checked ?? false}
                                        indeterminate={cb.defaultItem.indeterminate}
                                        onChange={handleChange}
                                        sx={{ margin: '-8px -5px -8px 0' }}
                                    />
                                }
                                label={<Typography variant='caption'>{cb.label}</Typography>}
                                sx={{ marginRight: '4px' }}
                            />;
                        })
                    }
                </FormGroup>
            </Grid>
        </Grid>
    );
};

export default React.memo(MCheckboxGroup);
